import { useState, useRef, useCallback, useMemo } from 'react';
import {
  EditorState,
  EditorTool,
  StabilizerCode,
  Stabilizer,
  LogicalOp,
  PauliTerm,
  SinglePauli,
  PauliType,
  STABILIZER_COLORS,
  DEFAULT_X_COLOR,
  DEFAULT_Z_COLOR,
  generateId,
  circuitToQiskit,
  buildDefaultCircuit,
} from '../../types/stabilizer';
import { verifyLogicalOperators } from '../../utils/stabilizerAnalysis';
import { TannerGraphSection, CheckMatrixSection, CodeParamsSection } from './CodeAnalysis';

interface Props {
  code: StabilizerCode;
  editorState: EditorState;
  onCodeChange: (code: StabilizerCode) => void;
  onEditorStateChange: (state: EditorState) => void;
  onEditCircuit: (stabId: string) => void;
  onFlippedStabilizersChange?: (ids: Set<string>) => void;
}

const TOOLS: { key: EditorTool; label: string; icon: string; tip: string }[] = [
  { key: 'select', label: 'Select', icon: '\u22b9', tip: 'Select & move qubits (V)' },
  { key: 'pan', label: 'Move', icon: '\u2725', tip: 'Drag to pan canvas (H)' },
  { key: 'addQubit', label: 'Qubit', icon: '\u25c9', tip: 'Click to place qubit (Q)' },
  { key: 'addStabilizer', label: 'Stabilizer', icon: '\u2b21', tip: 'Select qubits, then create (S)' },
];

export default function StabilizerToolbar({
  code,
  editorState,
  onCodeChange,
  onEditorStateChange,
  onEditCircuit,
  onFlippedStabilizersChange,
}: Props) {
  const [stabType, setStabType] = useState<PauliType>('Z');
  const [stabColor, setStabColor] = useState<string>(DEFAULT_Z_COLOR);

  // Drag-to-reorder state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragRef = useRef<number | null>(null);

  const setTool = (tool: EditorTool) => {
    onEditorStateChange({
      ...editorState,
      tool,
      edgeStartQubitId: null,
      selectedQubitIds: tool === 'addStabilizer' ? editorState.selectedQubitIds : new Set(),
    });
  };

  const createStabilizer = () => {
    if (editorState.selectedQubitIds.size < 1) return;
    const newId = generateId('s');
    const newStab: Stabilizer = {
      id: newId,
      type: stabType,
      qubitIds: Array.from(editorState.selectedQubitIds),
      color: stabColor,
    };
    const newSchedule = [...scheduleOrder, newId];
    onCodeChange({
      ...code,
      stabilizers: [...code.stabilizers, newStab],
      scheduleOrder: newSchedule,
    });
    onEditorStateChange({ ...editorState, selectedQubitIds: new Set() });
  };

  const updateStabilizer = (stabId: string, updates: Partial<Stabilizer>) => {
    onCodeChange({
      ...code,
      stabilizers: code.stabilizers.map((s) =>
        s.id === stabId ? { ...s, ...updates } : s
      ),
    });
  };

  const deleteStabilizer = (stabId: string) => {
    onCodeChange({
      ...code,
      stabilizers: code.stabilizers.filter((s) => s.id !== stabId),
      scheduleOrder: scheduleOrder.filter((id) => id !== stabId),
    });
    if (editorState.selectedStabilizerId === stabId) {
      onEditorStateChange({ ...editorState, selectedStabilizerId: null, hoveredStabilizerId: null });
    }
  };

  const selectStabilizer = (stabId: string) => {
    onEditorStateChange({
      ...editorState,
      tool: 'select',
      selectedStabilizerId: editorState.selectedStabilizerId === stabId ? null : stabId,
      selectedQubitIds: new Set(),
    });
  };

  const parseQubitIndices = (input: string): string[] | null => {
    const indices = input.split(/[,\s]+/).map((s) => s.trim()).filter((s) => s.length > 0).map(Number);
    if (indices.some(isNaN)) return null;
    const ids: string[] = [];
    for (const idx of indices) {
      const q = code.qubits.find((q) => q.index === idx);
      if (!q) return null;
      ids.push(q.id);
    }
    return ids;
  };

  const getQubitIndicesStr = (stab: Stabilizer): string => {
    return stab.qubitIds
      .map((id) => code.qubits.find((q) => q.id === id))
      .filter(Boolean)
      .sort((a, b) => a!.index - b!.index)
      .map((q) => q!.index)
      .join(', ');
  };

  const getDataIndices = (stab: Stabilizer): number[] => {
    return stab.qubitIds
      .map((id) => code.qubits.find((q) => q.id === id))
      .filter(Boolean)
      .map((q) => q!.index)
      .sort((a, b) => a - b);
  };

  // Schedule order (merged with stabilizer list)
  const scheduleOrder = code.scheduleOrder && code.scheduleOrder.length > 0
    ? code.scheduleOrder
    : code.stabilizers.map((s) => s.id);

  // Drag-to-reorder handlers
  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
    dragRef.current = idx;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback((idx: number) => {
    const fromIdx = dragRef.current;
    if (fromIdx === null || fromIdx === idx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }
    const order = [...scheduleOrder];
    const [moved] = order.splice(fromIdx, 1);
    order.splice(idx, 0, moved);
    onCodeChange({ ...code, scheduleOrder: order });
    setDragIdx(null);
    setDragOverIdx(null);
  }, [scheduleOrder, code, onCodeChange]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  return (
    <div className="flex flex-col gap-3 p-3 bg-gray-900 h-full overflow-y-auto">
      {/* Construct Code */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Construct Code</h3>
        <div className="grid grid-cols-2 gap-1.5">
          {TOOLS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTool(t.key)}
              title={t.tip}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded text-sm font-medium transition-colors ${
                editorState.tool === t.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="text-base">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stabilizer creation panel */}
      {editorState.tool === 'addStabilizer' && (
        <div className="border border-gray-700 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">New Stabilizer</h3>
          <div className="mb-3 text-xs space-y-1.5">
            <div className={`flex items-start gap-2 ${editorState.selectedQubitIds.size === 0 ? 'text-yellow-400' : 'text-gray-500'}`}>
              <span className="font-bold shrink-0">1.</span><span>Click qubits on canvas to select</span>
            </div>
            <div className={`flex items-start gap-2 ${editorState.selectedQubitIds.size > 0 && editorState.selectedQubitIds.size < 2 ? 'text-yellow-400' : 'text-gray-500'}`}>
              <span className="font-bold shrink-0">2.</span><span>Choose type and color</span>
            </div>
            <div className={`flex items-start gap-2 ${editorState.selectedQubitIds.size >= 2 ? 'text-yellow-400' : 'text-gray-500'}`}>
              <span className="font-bold shrink-0">3.</span><span>Click "Create Stabilizer"</span>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            {(['X', 'Z'] as PauliType[]).map((t) => (
              <button
                key={t}
                onClick={() => { setStabType(t); setStabColor(t === 'X' ? DEFAULT_X_COLOR : DEFAULT_Z_COLOR); }}
                className={`flex-1 py-1.5 rounded text-sm font-bold transition-colors ${stabType === t ? (t === 'X' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white') : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
              >{t}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {STABILIZER_COLORS.map((c) => (
              <button key={c} onClick={() => setStabColor(c)}
                className={`w-6 h-6 rounded-sm border-2 transition-transform ${stabColor === c ? 'border-white scale-110' : 'border-transparent hover:border-gray-500'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="text-xs text-gray-400 mb-2">{editorState.selectedQubitIds.size} qubit(s) selected</div>
          <button onClick={createStabilizer} disabled={editorState.selectedQubitIds.size < 1}
            className="w-full py-2 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors">
            Create Stabilizer
          </button>
        </div>
      )}

      {/* Stabilizer list with drag-to-reorder */}
      <CollapsibleSection title={`Stabilizers (${code.stabilizers.length})`} defaultOpen={true}>
        <div className="space-y-1.5">
          {scheduleOrder.length === 0 ? (
            <div className="text-xs text-gray-600 italic">No stabilizers yet</div>
          ) : (
            scheduleOrder.map((stabId, idx) => {
              const stab = code.stabilizers.find((s) => s.id === stabId);
              if (!stab) return null;
              return (
                <StabilizerCard
                  key={stab.id}
                  stab={stab}
                  index={idx}
                  isSelected={editorState.selectedStabilizerId === stab.id}
                  qubitIndicesStr={getQubitIndicesStr(stab)}
                  dataIndices={getDataIndices(stab)}
                  nDataQubits={code.qubits.length}
                  isDragging={dragIdx === idx}
                  isDragOver={dragOverIdx === idx}
                  onSelect={() => selectStabilizer(stab.id)}
                  onUpdate={(updates) => updateStabilizer(stab.id, updates)}
                  onDelete={() => deleteStabilizer(stab.id)}
                  onParseQubits={parseQubitIndices}
                  onEditCircuit={() => onEditCircuit(stab.id)}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                />
              );
            })
          )}
        </div>
      </CollapsibleSection>

      {/* Logical Operators */}
      <LogicalOperatorsSection code={code} onCodeChange={onCodeChange} />

      {/* Code-level Analysis */}
      {code.qubits.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Code-Level Analysis</h3>
          <div className="space-y-2">
            <CollapsibleSection title="Tanner Graph" defaultOpen={false}>
              <TannerGraphSection code={code} onFlippedStabilizersChange={onFlippedStabilizersChange} />
            </CollapsibleSection>
            <CollapsibleSection title="Check Matrix (H)" defaultOpen={false}>
              <CheckMatrixSection code={code} />
            </CollapsibleSection>
            <CollapsibleSection title="Code Parameters [[n,k,d]]" defaultOpen={false}>
              <CodeParamsSection code={code} />
            </CollapsibleSection>
          </div>
        </div>
      )}

      {/* Code summary */}
      <div className="mt-auto border-t border-gray-800 pt-3">
        <div className="text-xs text-gray-400 space-y-0.5">
          <div>Qubits: {code.qubits.length} | Edges: {code.edges.length}</div>
          <div>
            Stabilizers: {code.stabilizers.length}
            <span className="text-gray-600 ml-1">
              (X:{code.stabilizers.filter((s) => s.type === 'X').length} Z:{code.stabilizers.filter((s) => s.type === 'Z').length})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Collapsible section wrapper ──

function CollapsibleSection({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-800 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-800/50 hover:bg-gray-800 text-xs font-semibold text-gray-400 uppercase tracking-wider transition-colors">
        {title}
        <span className={`text-gray-600 transition-transform ${open ? 'rotate-180' : ''}`}>&#9660;</span>
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

// ── Stabilizer card with drag handle and Qiskit preview ──

function StabilizerCard({
  stab,
  index,
  isSelected,
  qubitIndicesStr,
  dataIndices,
  nDataQubits,
  isDragging,
  isDragOver,
  onSelect,
  onUpdate,
  onDelete,
  onParseQubits,
  onEditCircuit,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  stab: Stabilizer;
  index: number;
  isSelected: boolean;
  qubitIndicesStr: string;
  dataIndices: number[];
  nDataQubits: number;
  isDragging: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<Stabilizer>) => void;
  onDelete: () => void;
  onParseQubits: (input: string) => string[] | null;
  onEditCircuit: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [editingQubits, setEditingQubits] = useState(false);
  const [qubitInput, setQubitInput] = useState(qubitIndicesStr);
  const [qubitError, setQubitError] = useState(false);

  const commitQubits = () => {
    const ids = onParseQubits(qubitInput);
    if (ids && ids.length > 0) {
      onUpdate({ qubitIds: ids, circuit: undefined, measurementCircuit: undefined });
      setQubitError(false);
    } else {
      setQubitError(true);
    }
    setEditingQubits(false);
  };

  // Generate Qiskit code preview
  const qiskitPreview = (() => {
    if (stab.circuit) {
      return circuitToQiskit(stab.circuit, stab.name || `${stab.type}-stab`);
    }
    // Generate from default
    const ancillaIdx = nDataQubits;
    const defaultCirc = buildDefaultCircuit(stab.type, dataIndices, ancillaIdx, stab.id);
    return circuitToQiskit(defaultCirc, stab.name || `${stab.type}-stab`);
  })();

  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(); }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`rounded-lg border p-2 text-xs transition-all ${
        isDragging ? 'opacity-40' : ''
      } ${isDragOver ? 'border-indigo-400 border-dashed' : ''} ${
        isSelected ? 'border-indigo-500 bg-indigo-950/40' : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
      }`}
    >
      {/* Header with drag handle */}
      <div className="flex items-center gap-1.5">
        {/* Expand/collapse chevron */}
        <button onClick={(e) => { e.stopPropagation(); onSelect(); }}
          className="shrink-0 text-gray-500 hover:text-gray-300 transition-transform"
          style={{ transform: isSelected ? 'rotate(180deg)' : 'rotate(0deg)' }}
          title={isSelected ? 'Collapse' : 'Expand'}>
          <svg width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        {/* Drag handle */}
        <span className="text-gray-600 cursor-grab active:cursor-grabbing shrink-0 select-none" title="Drag to reorder">
          <svg width="8" height="14" viewBox="0 0 8 14"><circle cx="2" cy="2" r="1.2" fill="currentColor"/><circle cx="6" cy="2" r="1.2" fill="currentColor"/><circle cx="2" cy="7" r="1.2" fill="currentColor"/><circle cx="6" cy="7" r="1.2" fill="currentColor"/><circle cx="2" cy="12" r="1.2" fill="currentColor"/><circle cx="6" cy="12" r="1.2" fill="currentColor"/></svg>
        </span>
        <span className="text-[10px] text-gray-600 w-4 shrink-0">{index + 1}</span>
        <button className="w-4 h-4 rounded-sm shrink-0 border border-white/20"
          style={{ backgroundColor: stab.color }}
          onClick={(e) => { e.stopPropagation(); const idx = STABILIZER_COLORS.indexOf(stab.color); onUpdate({ color: STABILIZER_COLORS[(idx + 1) % STABILIZER_COLORS.length] }); }}
          title="Cycle color" />
        <button className="flex-1 text-left text-gray-200 truncate hover:text-white" onClick={onSelect} title="Select on canvas">
          {stab.name || `Stab-${stab.id.slice(-4)}`}
        </button>
        <button onClick={() => onUpdate({ type: stab.type === 'X' ? 'Z' : 'X', circuit: undefined, measurementCircuit: undefined })}
          className={`px-1.5 py-0.5 rounded font-bold text-[10px] shrink-0 ${stab.type === 'X' ? 'bg-purple-600/80 text-purple-100' : 'bg-blue-600/80 text-blue-100'}`}
          title="Toggle X/Z (resets circuit)">{stab.type}</button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-red-400 hover:bg-red-900/30 transition-colors" title="Delete">
          <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" /></svg>
        </button>
      </div>

      {/* Expanded details: qubit indices, name, edit circuit, qiskit preview */}
      {isSelected && (
        <>
      {/* Qubit indices */}
      <div className="flex items-center gap-1 mt-1 mb-1">
        <span className="text-gray-500 shrink-0">Qubits:</span>
        {editingQubits ? (
          <input autoFocus value={qubitInput}
            onChange={(e) => { setQubitInput(e.target.value); setQubitError(false); }}
            onBlur={commitQubits}
            onKeyDown={(e) => { if (e.key === 'Enter') commitQubits(); if (e.key === 'Escape') { setEditingQubits(false); setQubitError(false); } }}
            className={`flex-1 min-w-0 px-1 py-0.5 rounded bg-gray-900 border text-[11px] font-mono text-white focus:outline-none ${qubitError ? 'border-red-500' : 'border-gray-600 focus:border-indigo-500'}`} />
        ) : (
          <button onClick={() => { setQubitInput(qubitIndicesStr); setEditingQubits(true); setQubitError(false); }}
            className="flex-1 min-w-0 text-left font-mono text-gray-300 hover:text-white truncate" title="Click to edit">
            [{qubitIndicesStr}]
          </button>
        )}
      </div>
        <div className="mt-2 pt-2 border-t border-gray-700/50 space-y-2">
          <input type="text" placeholder="Name (optional)" value={stab.name || ''}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="w-full px-1.5 py-1 rounded bg-gray-900 border border-gray-700 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500" />
          <div className="flex flex-wrap gap-1">
            {STABILIZER_COLORS.map((c) => (
              <button key={c} onClick={() => onUpdate({ color: c })}
                className={`w-5 h-5 rounded-sm border-2 transition-transform ${stab.color === c ? 'border-white scale-110' : 'border-transparent hover:border-gray-500'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>

          {/* Edit Circuit button */}
          <button
            onClick={onEditCircuit}
            className="w-full py-1.5 rounded bg-gray-800 hover:bg-gray-700 border border-gray-600 text-xs text-gray-200 font-medium transition-colors flex items-center justify-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 6h3M5 3v6M8 6h3" strokeLinecap="round"/><circle cx="5" cy="3" r="1.5"/><circle cx="5" cy="9" r="1.5"/></svg>
            Edit Stabilizer Measurement Circuit
            {stab.circuit && <span className="text-[10px] text-yellow-500">(custom)</span>}
          </button>

          {/* Qiskit code preview */}
          <div>
            <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Qiskit Preview</div>
            <pre className="bg-gray-950 border border-gray-800 rounded p-1.5 text-[10px] font-mono text-gray-400 overflow-x-auto whitespace-pre leading-relaxed max-h-28 overflow-y-auto">
              {qiskitPreview}
            </pre>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

// ── Logical Operators Section ──

/** Parse a Pauli string like "X0 Z1 Y2" or "X0 X1 X2" into PauliTerm[] */
function parsePauliString(input: string, validIndices: Set<number>): PauliTerm[] | null {
  const tokens = input.trim().split(/[\s,]+/).filter((s) => s.length > 0);
  if (tokens.length === 0) return null;

  const terms: PauliTerm[] = [];
  for (const tok of tokens) {
    const match = tok.match(/^([XYZxyz])(\d+)$/);
    if (!match) return null;
    const pauli = match[1].toUpperCase() as SinglePauli;
    const idx = parseInt(match[2], 10);
    if (!validIndices.has(idx)) return null;
    terms.push({ pauli, qubitIndex: idx });
  }
  return terms;
}

/** Serialize PauliTerm[] to display string */
function termsToString(terms: PauliTerm[]): string {
  return terms.map((t) => `${t.pauli}${t.qubitIndex}`).join(' ');
}

function LogicalOperatorsSection({ code, onCodeChange }: { code: StabilizerCode; onCodeChange: (c: StabilizerCode) => void }) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('X\u0304');
  const [newInput, setNewInput] = useState('');
  const [inputError, setInputError] = useState(false);

  const ops = code.logicalOperators ?? [];
  const validIndices = useMemo(() => new Set(code.qubits.map((q) => q.index)), [code.qubits]);

  const verification = useMemo(() => verifyLogicalOperators(code), [code]);

  const addOp = () => {
    const terms = parsePauliString(newInput, validIndices);
    if (!terms) { setInputError(true); return; }
    const newOp: LogicalOp = { id: generateId('lo'), label: newLabel, terms };
    onCodeChange({ ...code, logicalOperators: [...ops, newOp] });
    setNewInput('');
    setAdding(false);
    setInputError(false);
  };

  const removeOp = (opId: string) => {
    onCodeChange({ ...code, logicalOperators: ops.filter((o) => o.id !== opId) });
  };

  const updateOpTerms = (opId: string, input: string): boolean => {
    const terms = parsePauliString(input, validIndices);
    if (!terms) return false;
    onCodeChange({
      ...code,
      logicalOperators: ops.map((o) => o.id === opId ? { ...o, terms } : o),
    });
    return true;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Logical Operators ({ops.length})
        </h3>
        {ops.length > 0 && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            verification.allValid ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
          }`}>
            {verification.allValid ? '\u2713 Valid' : '\u2717 Issues'}
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        {ops.map((op, idx) => {
          const v = verification.verifications[idx];
          return (
            <LogicalOpCard
              key={op.id}
              op={op}
              verification={v}
              allOps={ops}
              onRemove={() => removeOp(op.id)}
              onUpdateTerms={(s) => updateOpTerms(op.id, s)}
            />
          );
        })}
      </div>

      {/* Add button / form */}
      {adding ? (
        <div className="mt-2 border border-gray-700 rounded-lg p-2 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-400">Label:</span>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              className="w-16 px-1 py-0.5 rounded bg-gray-900 border border-gray-600 text-[11px] font-mono text-white focus:outline-none focus:border-indigo-500" />
          </div>
          <input
            autoFocus
            value={newInput}
            onChange={(e) => { setNewInput(e.target.value); setInputError(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addOp();
              if (e.key === 'Escape') { setAdding(false); setNewInput(''); setInputError(false); }
            }}
            placeholder="Pauli string (e.g. X0 X1 X2)"
            className={`w-full px-1.5 py-1 rounded bg-gray-900 border text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none ${
              inputError ? 'border-red-500' : 'border-gray-600 focus:border-indigo-500'
            }`}
          />
          {inputError && (
            <p className="text-[10px] text-red-400">Format: X0 Z1 Y2 (Pauli + qubit index)</p>
          )}
          <div className="flex gap-1">
            <button onClick={addOp}
              className="flex-1 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium transition-colors">
              Add
            </button>
            <button onClick={() => { setAdding(false); setNewInput(''); setInputError(false); }}
              className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 text-[11px] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => { setAdding(true); setNewLabel('X\u0304'); }}
          className="w-full mt-2 py-1.5 rounded bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-[11px] font-medium transition-colors">
          + Add Logical Operator
        </button>
      )}
    </div>
  );
}

function LogicalOpCard({ op, verification, allOps, onRemove, onUpdateTerms }: {
  op: LogicalOp;
  verification?: { commutesWithAll: boolean; notInStabilizerGroup: boolean; anticommutePairIdx: number | null };
  allOps: LogicalOp[];
  onRemove: () => void;
  onUpdateTerms: (s: string) => boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const commit = () => {
    if (!onUpdateTerms(input)) {
      setError(true);
    } else {
      setEditing(false);
      setError(false);
    }
  };

  const pairOp = verification?.anticommutePairIdx !== null && verification?.anticommutePairIdx !== undefined
    ? allOps[verification.anticommutePairIdx]
    : null;

  // Color Pauli terms: X=purple, Z=blue, Y=yellow
  const coloredTerms = op.terms.map((t, i) => {
    const color = t.pauli === 'X' ? 'text-purple-400' : t.pauli === 'Z' ? 'text-blue-400' : 'text-yellow-400';
    return <span key={i} className={color}>{t.pauli}{t.qubitIndex}</span>;
  });

  return (
    <div className="rounded-lg border border-gray-700/50 bg-gray-800/30 p-2 text-xs">
      <div className="flex items-center gap-1.5">
        <span className="font-bold text-[11px] text-indigo-300 shrink-0">
          {op.label}
        </span>
        <span className="text-gray-500">=</span>
        {editing ? (
          <input autoFocus value={input}
            onChange={(e) => { setInput(e.target.value); setError(false); }}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setError(false); } }}
            className={`flex-1 min-w-0 px-1 py-0.5 rounded bg-gray-900 border text-[11px] font-mono text-white focus:outline-none ${
              error ? 'border-red-500' : 'border-gray-600 focus:border-indigo-500'
            }`}
          />
        ) : (
          <button onClick={() => { setInput(termsToString(op.terms)); setEditing(true); setError(false); }}
            className="flex-1 min-w-0 text-left font-mono hover:text-white truncate text-[11px] flex gap-1" title="Click to edit">
            {coloredTerms}
          </button>
        )}
        <button onClick={onRemove}
          className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-red-400 hover:bg-red-900/30 transition-colors" title="Remove">
          <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="9" y2="9" /><line x1="9" y1="1" x2="1" y2="9" /></svg>
        </button>
      </div>

      {/* Verification badges */}
      {verification && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
            verification.commutesWithAll ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
          }`}>
            {verification.commutesWithAll ? '\u2713' : '\u2717'} Commutes
          </span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
            verification.notInStabilizerGroup ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
          }`}>
            {verification.notInStabilizerGroup ? '\u2713' : '\u2717'} Independent
          </span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
            verification.anticommutePairIdx !== null ? 'bg-green-900/40 text-green-400' : 'bg-yellow-900/40 text-yellow-400'
          }`}>
            {verification.anticommutePairIdx !== null ? '\u2713' : '?'} Pair
            {pairOp && <span className="ml-0.5 opacity-70">({pairOp.label})</span>}
          </span>
        </div>
      )}
    </div>
  );
}
